import { useNavigate } from "react-router-dom";
import {
  Sparkles,
  Lightbulb,
  Users,
  Palette,
  PiggyBank,
  Megaphone,
  Heart,
  School,
  ArrowRight,
  Rocket,
  ArrowLeft,
} from "lucide-react";
import BizBirdCard from "@/components/BizBirdCard";
import ResourceLibrary from "@/components/ResourceLibrary";
import {
  EDUCATOR_RESOURCES,
  EDUCATOR_RESOURCES_FOLDER,
  BUSINESS_TEMPLATES,
  BUSINESS_TEMPLATES_FOLDER,
} from "@/data/externalResources";
import { JR_BIRDS } from "@/utils/jrDemoState";

/**
 * Public landing page for "Lemonade Leaders Jr."
 *
 * Routes that mount this page:
 *   /lemonade-leaders-jr   (canonical)
 *   /elementary            (alias — see App.js Navigate)
 *
 * Strategic positioning page; lightweight, single-file, no auth required.
 */
export default function ElementaryLanding() {
  const navigate = useNavigate();

  const goPilot = () => {
    // Reuse existing contact form on /; prefill via querystring + hash.
    navigate("/?pilot=elementary#contact");
  };

  const outcomes = [
    { icon: <Lightbulb className="w-6 h-6" />, label: "Problem Solving" },
    { icon: <Users className="w-6 h-6" />, label: "Teamwork" },
    { icon: <Palette className="w-6 h-6" />, label: "Creativity" },
    { icon: <PiggyBank className="w-6 h-6" />, label: "Money Basics" },
    { icon: <Megaphone className="w-6 h-6" />, label: "Communication" },
    { icon: <Heart className="w-6 h-6" />, label: "Confidence" },
  ];

  const missions = [
    { n: 1, t: "Welcome to CEO City" },
    { n: 2, t: "Meet the Biz Birds" },
    { n: 3, t: "Find a Problem" },
    { n: 4, t: "Create Your Big Idea" },
    { n: 5, t: "Brand Your Business" },
    { n: 6, t: "Money Mission" },
    { n: 7, t: "Tell the World" },
    { n: 8, t: "Pitch Day" },
    { n: 9, t: "Junior CEO Certificate" },
  ];

  const venues = [
    "Elementary classrooms",
    "After-school programs",
    "Summer camps",
    "Libraries",
    "Youth organizations",
    "Family engagement events",
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Top bar */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium text-sm"
            data-testid="elementary-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </button>
          <button
            onClick={goPilot}
            className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-full font-semibold text-sm sm:text-base transition-colors"
            data-testid="elementary-nav-pilot"
          >
            Join the Pilot
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-28 pb-20 bg-gradient-to-b from-yellow-50 via-amber-50 to-white">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <Sparkles className="w-4 h-4" />
            New · Pilot Program · Grades 2–5
          </div>
          <p className="text-yellow-700 font-bold tracking-wide uppercase text-sm mb-4">
            Lemonade Leaders Jr.
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Big Ideas Start <span className="text-yellow-500">Small.</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto mb-8 leading-relaxed">
            A Mini CEO Adventure for grades 2–5 where students build confidence,
            teamwork, creativity, communication, and early business skills through
            a fun virtual entrepreneurship journey.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={goPilot}
              className="bg-yellow-500 hover:bg-yellow-600 text-white px-8 py-4 rounded-full font-bold text-lg transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              data-testid="elementary-hero-pilot"
            >
              <Rocket className="w-5 h-5" />
              Join the Elementary Pilot List
            </button>
            <button
              onClick={() => navigate("/elementary/demo")}
              className="bg-white hover:bg-gray-50 text-gray-800 px-8 py-4 rounded-full font-bold text-lg transition-all border-2 border-yellow-300 flex items-center justify-center gap-2"
              data-testid="elementary-hero-demo"
            >
              Preview Student Demo
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
          <p className="mt-6 text-sm text-gray-500">
            Powered by <span className="font-semibold text-gray-700">LemonBot Jr.</span>
          </p>
        </div>
      </section>

      {/* Section 1 — What is Lemonade Leaders Jr.? */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            What Is Lemonade Leaders Jr.?
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Lemonade Leaders Jr. is an elementary entrepreneurship and career
            readiness experience that helps students become Junior CEOs through
            story-based missions, Biz Bird teamwork, LemonBot Jr. guidance,
            simple money skills, branding activities, and a mini pitch challenge.
          </p>
        </div>
      </section>

      {/* Section 2 — What Students Learn */}
      <section className="py-20 bg-gradient-to-b from-yellow-50 to-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-12">
            What Students Learn
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {outcomes.map((o) => (
              <div
                key={o.label}
                className="bg-white rounded-2xl p-6 ring-1 ring-yellow-100 shadow-sm hover:shadow-md transition-shadow flex items-center gap-4"
              >
                <div className="bg-yellow-500 text-white p-3 rounded-xl">{o.icon}</div>
                <span className="text-lg font-semibold text-gray-900">{o.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3 — Meet the Biz Birds */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              Meet the Biz Birds
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Every great team has different strengths. Junior CEOs find their
              bird and learn how each one helps the team take off.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {Object.entries(JR_BIRDS).map(([key, b]) => (
              <BizBirdCard
                key={key}
                emoji={b.emoji}
                image={b.image}
                name={b.name}
                role={b.role}
                description={b.description}
                testId={`elementary-bird-${key}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Section 4 — Student Missions */}
      <section className="py-20 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              The Junior CEO Journey
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Nine story-based missions take young learners from "What's a
              business?" to "I just gave my first pitch."
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {missions.map((m) => (
              <div
                key={m.n}
                className="bg-white rounded-2xl p-5 ring-1 ring-yellow-100 shadow-sm flex items-center gap-4"
              >
                <span className="bg-yellow-500 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold shadow shrink-0">
                  {m.n}
                </span>
                <div>
                  <p className="text-xs text-yellow-700 font-semibold uppercase tracking-wide">
                    Mission {m.n}
                  </p>
                  <p className="text-base font-bold text-gray-900 leading-tight">
                    {m.t}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 5 — Built for Schools and Youth Programs */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-3xl p-8 sm:p-12 ring-1 ring-yellow-100">
            <div className="flex items-center gap-3 mb-4">
              <School className="w-8 h-8 text-yellow-600" />
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Built for Schools and Youth Programs
              </h2>
            </div>
            <p className="text-gray-600 text-lg mb-8">
              Lemonade Leaders Jr. fits beautifully into a wide range of
              learning environments:
            </p>
            <ul className="grid sm:grid-cols-2 gap-3">
              {venues.map((v) => (
                <li
                  key={v}
                  className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 ring-1 ring-yellow-100"
                >
                  <span className="bg-green-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold shrink-0">
                    ✓
                  </span>
                  <span className="text-gray-800 font-medium">{v}</span>
                </li>
              ))}
            </ul>
            <p className="mt-8 text-sm text-gray-500 italic">
              <strong>Note for educators &amp; admins:</strong> Lemonade
              Leaders Jr. is currently in pilot development. This pathway is
              designed for grades 2–5 and can be used for demos, camps,
              after-school programs, and future school pilots.
            </p>
          </div>
        </div>
      </section>

      {/* Built for Pilots — concise pilot-readiness card */}
      <section className="py-14 bg-white" id="built-for-pilots">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl ring-1 ring-green-200 p-7 sm:p-9">
            <div className="flex items-center gap-3 mb-3">
              <span className="bg-green-500 text-white w-10 h-10 rounded-2xl flex items-center justify-center shadow-sm" aria-hidden="true">
                <Sparkles className="w-5 h-5" />
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Built for Pilots
              </h2>
            </div>
            <p className="text-gray-700 text-base sm:text-lg mb-5">
              Pilot-ready for elementary classrooms, after-school programs,
              summer camps, youth organizations, and family engagement events.
            </p>
            <ul className="grid sm:grid-cols-3 gap-3" data-testid="elementary-built-for-pilots">
              {[
                "No student login required for demo previews",
                "Simple entrepreneurship missions for grades 2–5",
                "Pilot interest captured through the Elementary Pilot List",
              ].map((bullet) => (
                <li
                  key={bullet}
                  className="flex items-start gap-3 bg-white rounded-xl px-4 py-3 ring-1 ring-green-100"
                >
                  <span className="bg-green-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 mt-0.5">
                    ✓
                  </span>
                  <span className="text-sm text-gray-800 font-medium leading-relaxed">
                    {bullet}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Section 5.5 — Resources for educators, admins, and parents */}
      <section className="py-14 bg-gradient-to-br from-slate-50 to-yellow-50" id="elementary-resources">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              Resources for Educators &amp; Parents
            </h2>
            <p className="text-gray-700 max-w-2xl mx-auto">
              Facilitator guides, classroom activities, and student worksheets — open them straight from our shared Drive folders.
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <ResourceLibrary
              title="Educator Resources"
              description="Facilitator guides, classroom activities, sponsorship deck, and event-planning kits."
              folderUrl={EDUCATOR_RESOURCES_FOLDER}
              files={EDUCATOR_RESOURCES}
              testId="elementary-landing-educator-resources"
              accent="sky"
            />
            <ResourceLibrary
              title="Business Templates"
              description="Student-friendly worksheets, simple calculators, and pitch templates kids can use at home or in class."
              folderUrl={BUSINESS_TEMPLATES_FOLDER}
              files={BUSINESS_TEMPLATES}
              testId="elementary-landing-business-templates"
              accent="yellow"
            />
          </div>
        </div>
      </section>

      {/* Section 6 — CTA */}
      <section className="py-20 bg-gradient-to-r from-yellow-500 to-orange-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Bring Lemonade Leaders Jr. to Your Students?
          </h2>
          <p className="text-yellow-50 text-lg mb-8 max-w-2xl mx-auto">
            Pilot schools, camps, and youth programs get early access, founder
            support, and a chance to shape the elementary experience.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={goPilot}
              className="bg-white text-yellow-600 hover:bg-yellow-50 px-8 py-4 rounded-full font-bold text-lg transition-all shadow-lg flex items-center justify-center gap-2"
              data-testid="elementary-cta-pilot"
            >
              <Rocket className="w-5 h-5" />
              Join the Elementary Pilot List
            </button>
            <button
              onClick={() => navigate("/elementary/demo")}
              className="bg-white/10 hover:bg-white/20 text-white border-2 border-white px-8 py-4 rounded-full font-bold text-lg transition-all flex items-center justify-center gap-2"
              data-testid="elementary-cta-demo"
            >
              Preview Student Demo
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
